import { useEffect, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { MeResponse, Person, PersonBlockRole } from "../../api/types";
import { UserRefChip } from "../../components/UserRefChip";
import { formatNumber } from "../../lib/format";
import { useRouter } from "../../router/Router";
import { type AchievementSort, achievementPct } from "./achievement";
import { AchievementTable, defaultAchievementSort } from "./AchievementTable";
import { levelLabel, OverdueChip, statusLabel } from "./BlockCard";
import { type BlockSortKey, sortBlocks } from "./blockSort";
import { BlockSortSelect } from "./BlockSortSelect";
import { dueDateLabel } from "./dueDate";
import { filterPeopleByQuery } from "./peopleSearch";
import { pillarTint } from "./pillarColors";
import { SeasonPicker } from "./SeasonPicker";
import { useBlockSeason } from "./useBlockSeason";

interface State {
  status: "loading" | "ready" | "error";
  people: Person[];
  teams: string[];
  // The season the server resolved for the last successful fetch (empty when no
  // season is active) — distinguishes "no active season" from "active, no people".
  resolvedSeasonId: string;
  errorMessage: string | null;
}

const initial: State = {
  status: "loading",
  people: [],
  teams: [],
  resolvedSeasonId: "",
  errorMessage: null
};

// A block may relate to a person via more than one role, so a person's blocks
// are shown grouped under each role that ties them to it (Assigned / Owns /
// Contributes). The backend never duplicates a card — the roles ride on the
// block, and a dual-role block simply appears under both headings.
const roleSections: { role: PersonBlockRole; label: string }[] = [
  { role: "assignee", label: "Assigned" },
  { role: "exec_owner", label: "Owns" },
  { role: "contributor", label: "Contributes" }
];

// People pivots the season from blocks to the people doing the work: one card
// per person with their earned total, an assigned-vs-complete summary, and their
// blocks grouped by role. The season defaults to the active one (leadership can
// switch it via the SeasonPicker); the team filter is server-side so the counts
// and earned stay consistent.
export function PeoplePage({ me }: { me: MeResponse }) {
  const router = useRouter();
  const [state, setState] = useState<State>(initial);
  const { seasonId, seasons, setSeasonId, ready } = useBlockSeason(me);
  const [team, setTeam] = useState<string>("");
  const [sort, setSort] = useState<AchievementSort>(defaultAchievementSort);
  const [blockSort, setBlockSort] = useState<BlockSortKey>("size");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!ready) {
      return;
    }
    // Guard against a stale response landing after a newer season/team switch:
    // rapid changes fire overlapping fetches, and only the latest may win (same
    // pattern as useBlockSeason).
    let cancelled = false;
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));
    apiClient
      .listBlockPeople({ season_id: seasonId, team })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setState({
          status: "ready",
          people: result.people,
          teams: result.teams,
          resolvedSeasonId: result.season_id,
          errorMessage: null
        });
      })
      .catch((caught) => {
        if (cancelled) {
          return;
        }
        // Keep the last-known team options so the dropdown value never points at
        // an option that vanished on error.
        setState((previous) => ({
          status: "error",
          people: [],
          teams: previous.teams,
          resolvedSeasonId: previous.resolvedSeasonId,
          errorMessage: messageFor(caught)
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [seasonId, team, ready]);

  const shown = filterPeopleByQuery(state.people, query);

  return (
    <div className="blocks-view">
      <section className="card">
        <h2>Contributors</h2>
        <div className="form-row form-row--inline">
          <SeasonPicker
            seasons={seasons}
            value={seasonId}
            onChange={(next) => {
              // A team present in one season may not exist in another; reset the
              // filter so the dropdown value never points at a missing option.
              setSeasonId(next);
              setTeam("");
            }}
          />
          <label>
            Team
            <select value={team} onChange={(event) => setTeam(event.target.value)}>
              <option value="">All teams</option>
              {state.teams.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <BlockSortSelect value={blockSort} onChange={setBlockSort} />
          <label>
            Search
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name or email"
            />
          </label>
        </div>

        {state.status === "loading" && <p className="loading">Loading…</p>}
        {state.status === "error" && state.errorMessage !== null && (
          <p className="form-row__error">{state.errorMessage}</p>
        )}
        {state.status === "ready" && state.people.length === 0 && (
          <p className="muted">{emptyMessage(state.resolvedSeasonId, team)}</p>
        )}
        {state.status === "ready" && state.people.length > 0 && shown.length === 0 && (
          <p className="muted">No people match your search.</p>
        )}
        {state.status === "ready" && shown.length > 0 && (
          <AchievementTable people={shown} sort={sort} onSortChange={setSort} />
        )}
        {state.status === "ready" && shown.length > 0 && (
          <div className="people-card-grid">
            {shown.map((person) => (
              <PersonCard
                key={person.user.id}
                person={person}
                blockSort={blockSort}
                onOpenBlock={(id) => router.navigate(`/blocks/${id}`)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PersonCard({
  person,
  blockSort,
  onOpenBlock
}: {
  person: Person;
  blockSort: BlockSortKey;
  onOpenBlock: (id: string) => void;
}) {
  const pct = achievementPct(person.assigned_complete, person.assigned_total);

  return (
    <div className="card person-card">
      <div className="person-card__head">
        <UserRefChip user={person.user} />
        <div className="person-card__earned">
          <div className="person-card__earned-value">{formatNumber(Math.round(person.earned))}</div>
          <div className="person-card__earned-label">season earned</div>
        </div>
      </div>

      {person.assigned_total > 0 && (
        <>
          <div className="person-card__summary">
            {person.assigned_complete} of {person.assigned_total} assigned{" "}
            {person.assigned_total === 1 ? "block" : "blocks"} complete
          </div>
          <div className="block-progress">
            <div
              className={`block-progress__fill${pct >= 100 ? " is-complete" : ""}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </>
      )}

      {roleSections.map((section) => {
        const blocks = sortBlocks(
          person.blocks.filter((block) => block.roles.includes(section.role)),
          blockSort
        );
        if (blocks.length === 0) {
          return null;
        }
        return (
          <div className="person-card__role" key={section.role}>
            <div className="person-card__role-label">{section.label}</div>
            <ul className="person-card__blocks">
              {blocks.map((block) => (
                <li key={block.id}>
                  <button
                    type="button"
                    className="person-block"
                    style={{ "--pillar-tint": pillarTint(block.pillar_id, block.pillar) } as React.CSSProperties}
                    onClick={() => onOpenBlock(block.id)}
                  >
                    <span className="person-block__name">
                      {block.path.length > 0 && (
                        <span className="person-block__path">{block.path.join(" › ")}</span>
                      )}
                      {block.name}
                    </span>
                    <span className="person-block__meta">
                      <span className="chip">{levelLabel(block.level)}</span>
                      <span className={`chip chip--status status-${block.status}`}>
                        {statusLabel(block.status)}
                      </span>
                      <span className="chip">{dueDateLabel(block.due_date)}</span>
                      <OverdueChip dueDate={block.due_date} status={block.status} />
                      <span className="person-block__size">{block.size}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// emptyMessage distinguishes an empty team filter, a season that resolved but has
// no people yet, and the default view when no season is active at all. It keys on
// the server-resolved season id, so an active-but-empty season is never mislabeled
// "no active season".
function emptyMessage(resolvedSeasonId: string, team: string): string {
  if (team !== "") {
    return "No people match this team.";
  }
  if (resolvedSeasonId !== "") {
    return "This season has no people yet.";
  }
  return "No active blocks season yet — nothing to show.";
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Operation failed.";
}
